/**
 * Text metrics for the print grid (spec §4.2). The page parts are pure SVG with no layout engine
 * to measure against, so cell text is truncated against an estimate instead of a `clipPath` per
 * cell (a page carries ~36 x 11 of them).
 *
 * A single average glyph width was too coarse: at 6.6 px/char a Czech date (`5. 1. 2026`) measured
 * 66 px and was cut to `5. 1. 202…` inside a 72-unit column it actually fits. Splitting the
 * alphabet into narrow and wide glyphs is still an estimate, but it stops punctuation and spaces
 * from being charged as full-width characters.
 */
const NARROW_CHARS = " .,:;il|jtfrI'"
const NARROW_PX = 3.3
const WIDE_PX = 6.6
const ELLIPSIS = '…'

const charWidth = (ch: string): number => (NARROW_CHARS.indexOf(ch) >= 0 ? NARROW_PX : WIDE_PX)

/** Estimated rendered width of `text` at 12 px. */
export const textWidth = (text: string): number => {
  let w = 0
  for (let i = 0; i < text.length; i++) w += charWidth(text.charAt(i))
  return w
}

/** `text` if it fits `widthPx`, otherwise the longest prefix that fits alongside a trailing `…`. */
export const truncateToWidth = (text: string, widthPx: number): string => {
  if (widthPx < WIDE_PX) return ''
  if (textWidth(text) <= widthPx) return text
  const budget = widthPx - WIDE_PX
  let used = 0
  let cut = 0
  for (let i = 0; i < text.length; i++) {
    const w = charWidth(text.charAt(i))
    if (used + w > budget) break
    used += w
    cut = i + 1
  }
  return `${text.slice(0, cut)}${ELLIPSIS}`
}
